/**
 * HtmlRenderer — sandboxed HTML for the rare case Markdown can't carry the
 * partner's intent (rich tables with custom layout, inline SVG sparklines,
 * partner-supplied promo blocks).
 *
 * Sanitisation runs through DOMPurify on every render — we don't trust the
 * agent + we don't trust upstream tools. `sandbox: 'strict'` is the default
 * profile (text-level tags only); 'partner_css' permits inline `style`
 * attributes for partner-driven layout. `<script>`, `<iframe>`, event
 * handlers (`onclick=...`), `javascript:` URLs are stripped in both modes.
 *
 * Content goes inside a div with a fixed inner-html assignment via
 * `dangerouslySetInnerHTML` AFTER sanitisation — the name is scary but
 * this is the only safe pattern for arbitrary HTML in React.
 */
import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import type { HtmlContentItem } from '@wg/types';
import type { Renderer } from '../registry';

/**
 * Read the embed app's resolved theme tokens from the parent document's
 * <html> element. applyThemeToDocument has set CSS custom properties there
 * once the partner instance resolves; getComputedStyle returns whatever's
 * in effect right now.
 *
 * We pack the values into a <style> block injected into the iframe srcdoc
 * (via the document's `:root`) so the card's hard-coded `--fg`, `--muted`,
 * etc. get overridden by the partner palette. Last declaration wins for
 * custom props in the cascade, so this style block is appended *after* any
 * the card already includes.
 *
 * Iframes are sandboxed (`sandbox=""`) so they can't reach back into the
 * parent's stylesheet or compute parent values themselves — we have to
 * snapshot the values at React render time and bake them into srcdoc.
 */
function readThemeTokens(): Record<string, string> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return {};
  }
  const root = document.documentElement;
  const cs = window.getComputedStyle(root);
  // Only the tokens cards consume. Card HTML uses --fg/--muted/--line/
  // --glass/--glass-heavy historically; we map the embed-app's wg-* names
  // to those, AND re-export the wg-* names so partner-authored cards that
  // reference them directly also work.
  const get = (name: string) => cs.getPropertyValue(name).trim();
  return {
    '--fg': get('--wg-color-text') || 'rgba(255,255,255,.95)',
    '--muted': get('--wg-color-text-muted') || 'rgba(255,255,255,.65)',
    '--line': get('--wg-color-border') || 'rgba(255,255,255,.08)',
    '--glass': get('--wg-color-surface') || 'rgba(248,248,248,.04)',
    '--glass-heavy': get('--wg-color-surface') || 'rgba(248,248,248,.08)',
    '--wg-color-primary': get('--wg-color-primary') || '#69005b',
    '--wg-color-primary-contrast':
      get('--wg-color-primary-contrast') || '#ffffff',
    '--wg-color-bg': get('--wg-color-bg') || 'transparent',
    '--wg-color-surface': get('--wg-color-surface') || 'rgba(255,255,255,.06)',
    '--wg-color-text': get('--wg-color-text') || 'rgba(255,255,255,.94)',
    '--wg-color-text-muted':
      get('--wg-color-text-muted') || 'rgba(255,255,255,.66)',
    '--wg-color-border': get('--wg-color-border') || 'rgba(255,255,255,.14)',
    '--wg-color-gradient':
      get('--wg-color-gradient') ||
      'linear-gradient(135deg,#2c003e 0%,#d90091 100%)',
    '--wg-radius': get('--wg-radius') || '14px',
    '--wg-font-family':
      get('--wg-font-family') ||
      "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
  };
}

// Heuristic luminance check on a CSS color string. We only need to pick
// dark vs light to flip attribution logos and color-scheme; cheap relative
// luminance (sRGB-weighted, no gamma) is fine. Returns 'dark' for any
// unparseable input — defaults toward the WG seed (dark) so we don't
// mis-flip to a light scheme on a transparent / unknown bg.
function detectThemeFlavor(bg: string): 'light' | 'dark' {
  const m =
    bg.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/) ||
    bg.match(/rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/) ||
    bg.match(/^#([0-9a-f]{6})/i);
  let r = 0,
    g = 0,
    b = 0;
  if (!m) return 'dark';
  if (m.length >= 4) {
    r = parseInt(m[1], 10);
    g = parseInt(m[2], 10);
    b = parseInt(m[3], 10);
  } else {
    const hex = m[1];
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  }
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? 'light' : 'dark';
}

function injectThemeIntoSrcDoc(html: string): string {
  const tokens = readThemeTokens();
  const flavor = detectThemeFlavor(tokens['--wg-color-bg'] || '');
  const decls = Object.entries(tokens)
    .map(([k, v]) => `  ${k}:${v};`)
    .join('\n');
  // Wrap in :root so it cascades to every element. body color/font sets
  // a sane default for cards that forgot to bind to --fg / --wg-font-family.
  // color-scheme tells the iframe's own user-agent rules (default form
  // controls, scrollbars) to render in the right flavor.
  const themeStyle =
    `<style id="wg-embed-theme">\n` +
    `:root {\n${decls}\n  color-scheme: ${flavor};\n}\n` +
    `body { color: var(--fg); font-family: var(--wg-font-family); background: transparent; }\n` +
    `a { color: var(--wg-color-primary); }\n` +
    `</style>`;
  // Inject just before </head> if present so it lands after any card
  // styles. Falls back to before </body> or prepend if neither exists.
  let out = html;
  if (/<\/head>/i.test(out)) {
    out = out.replace(/<\/head>/i, `${themeStyle}</head>`);
  } else if (/<\/body>/i.test(out)) {
    out = out.replace(/<\/body>/i, `${themeStyle}</body>`);
  } else {
    out = themeStyle + out;
  }
  // Tag the <html> root with data-theme so the card's own CSS can target
  // light/dark variants (e.g. attribution logo selection in html_renderer.py).
  if (/<html\b/i.test(out)) {
    out = out.replace(
      /<html\b([^>]*)>/i,
      (_match, attrs) => `<html data-theme="${flavor}"${attrs}>`,
    );
  }
  return out;
}

const STRICT_CONFIG = {
  ALLOWED_TAGS: [
    'a',
    'b',
    'strong',
    'i',
    'em',
    'u',
    'br',
    'p',
    'span',
    'div',
    'ul',
    'ol',
    'li',
    'blockquote',
    'code',
    'pre',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'table',
    'thead',
    'tbody',
    'tr',
    'td',
    'th',
    'hr',
    'img',
  ],
  ALLOWED_ATTR: ['href', 'title', 'alt', 'src', 'rel', 'target', 'class'],
  ALLOWED_URI_REGEXP:
    /^(?:https?:|data:image\/(?:png|jpe?g|gif|webp|svg\+xml);|mailto:|tel:|#)/i,
};

const PARTNER_CSS_CONFIG = {
  ...STRICT_CONFIG,
  ALLOWED_ATTR: [...STRICT_CONFIG.ALLOWED_ATTR, 'style'],
};

const HtmlRenderer: Renderer = ({ item }) => {
  const it = item as HtmlContentItem & {
    src?: { html?: string };
  };
  // Tools that came through html_renderer.py (place/weather/transit cards)
  // ship their HTML at `src.html` as a full HTML document (with <head>,
  // <style>, etc.) for the RN app's WebView. New direct-html items use
  // top-level `html` and are typically inline fragments. Distinguish:
  //   - Full document → render via sandboxed iframe srcdoc (matches RN's
  //     WebView; <style> tags survive, scripts are still blocked by the
  //     sandbox attribute, no parent-page access).
  //   - Inline fragment → DOMPurify-sanitize and dangerouslySetInnerHTML.
  const html = it.html ?? it.src?.html ?? '';
  const isFullDocument = /<!doctype|<html\b/i.test(html.slice(0, 200));
  const safe = useMemo(() => {
    if (!html) return '';
    const cfg =
      it.sandbox === 'partner_css' ? PARTNER_CSS_CONFIG : STRICT_CONFIG;
    // Force links to open in a new tab + drop the opener so a malicious
    // href can't reach back into our window. DOMPurify doesn't add this
    // for us — we patch via an after-sanitise hook scoped to this call.
    DOMPurify.addHook('afterSanitizeAttributes', node => {
      if (node instanceof HTMLAnchorElement) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    });
    try {
      return DOMPurify.sanitize(html, cfg);
    } finally {
      DOMPurify.removeHook('afterSanitizeAttributes');
    }
  }, [html, it.sandbox]);

  // Theme-inject the srcdoc once per render — readThemeTokens() snapshots
  // current parent <html> custom-prop values. If the partner theme changes
  // mid-session (rare), React re-renders and we resnapshot. useMemo keeps
  // us from rebuilding the same string when neither html nor the theme
  // tokens changed (we tie the dep on `html` only — token changes don't
  // happen post-mount in practice).
  const themedHtml = useMemo(
    () => (isFullDocument ? injectThemeIntoSrcDoc(html) : html),
    [html, isFullDocument],
  );

  if (isFullDocument) {
    // Sandboxed iframe — no allow-same-origin, no allow-scripts. The
    // document renders its own <style> + our injected theme tokens.
    // estimated_height_px is honored when the producer knows the rough
    // size; otherwise we let the iframe auto-resize via a ResizeObserver
    // inside the body.
    return (
      <iframe
        title="card"
        className="wg-chat__html-frame"
        sandbox=""
        srcDoc={themedHtml}
        style={{
          width: '100%',
          border: 0,
          display: 'block',
          minHeight: it.estimated_height_px
            ? `${it.estimated_height_px}px`
            : '220px',
          background: 'transparent',
        }}
      />
    );
  }
  if (!safe) return null;
  return (
    <div
      className="wg-chat__html"
      style={
        it.estimated_height_px
          ? { minHeight: `${it.estimated_height_px}px` }
          : undefined
      }
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
};

export default HtmlRenderer;
